import React, { useState, useMemo, useCallback, useEffect } from "react";
import { Button } from "@shared/ui/button";
import {
  ChevronRight,
  Plus,
  Trash2,
  Camera,
  RefreshCw,
  Clock,
  Pause,
} from "lucide-react";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField } from "@shared/services/FormServices";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSopTaskOptions, useUpdateSopTasks } from "../../../hooks"; // Your API hook
import { useSkillOptions } from "../../../hooks"; // Skills API hook
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useToast } from "@shared/hooks/use-toast";
import { UpdateSopTasksRequest } from "@features/wx/master/types";

// Schema for the entire form
const tasksSchema = z
  .object({
    // Dynamic task fields will be added at runtime
  })
  .catchall(z.any()); // Allow dynamic fields

interface SopTasksStepProps {
  state: any;
  onUpdateState: (updates: any) => void;
  onNext?: () => void;
  onPrevious?: () => void;
  readOnly?: boolean;
  templateId: string;
  onStepSave?: (stepIndex: number, data: any, isDirty: boolean) => void;
}

const SopTasksStep: React.FC<SopTasksStepProps> = ({
  state,
  onUpdateState,
  onNext,
  onPrevious,
  readOnly = false,
  templateId,
  onStepSave,
}) => {
  // Get operation type from basic info step
  const operationType = state.basicInfo?.operationType || "";
  const sopId = state.basicInfo?.sopId || templateId;
  const utilityId = useRemoteUtilityId();
  const [isManualAdd, setIsManualAdd] = useState(false);
  const [isManualDelete, setIsManualDelete] = useState(false);

  const { toast } = useToast();

  // Remove the showTaskForm state since we always show cards
  const [selectedFilter, setSelectedFilter] = useState<string>("all");
  const [isFormInitialized, setIsFormInitialized] = useState(false);

  // Add the update SOP tasks hook
  const updateSopTasksMutation = useUpdateSopTasks();

  // Fetch API tasks based on operation type
  const {
    options: apiTasks,
    isLoading: loadingApiTasks,
    error: apiTasksError,
  } = useSopTaskOptions(operationType);

  // Fetch skills from API
  const {
    options: skillsOptions,
    isLoading: loadingSkills,
    getSkillNameById,
    getSkillIdByName,
  } = useSkillOptions();

  console.log("SKILLS", skillsOptions);

  // ✅ FIXED: Create default values for form with proper handling
  const createDefaultValues = useCallback(() => {
    const defaultValues: any = {};
    const tasks = Array.isArray(state.tasks) ? state.tasks : [];

    if (tasks.length === 0) {
      console.log("⚠️ No tasks found or tasks is not an array");
      return defaultValues;
    }

    // Add task field default values
    tasks.forEach((task: any) => {
      defaultValues[`task_${task.id}_title`] = task.title || "";
      defaultValues[`task_${task.id}_duration`] = task.duration || 0;
      defaultValues[`task_${task.id}_downtime`] = task.downtime || 0;
      defaultValues[`task_${task.id}_skills`] = Array.isArray(task.skills)
        ? task.skills
        : [];
      defaultValues[`task_${task.id}_mandatory`] = task.mandatory || false;
      defaultValues[`task_${task.id}_photoRequired`] =
        task.photoRequired || false;
    });

    return defaultValues;
  }, [state.tasks]);

  const tasksForm = useForm<z.infer<typeof tasksSchema>>({
    resolver: zodResolver(tasksSchema),
    defaultValues: createDefaultValues(),
  });

  // ✅ FIXED: Calculate total TAT and downtime using both state and form values
  const { totalTAT, totalDowntime } = useMemo(() => {
    const tasks = Array.isArray(state.tasks) ? state.tasks : [];
    const formValues = tasksForm.getValues();

    let totalTAT = 0;
    let totalDowntime = 0;

    tasks.forEach((task: any) => {
      // Get duration from form first, then fallback to state
      const duration =
        formValues[`task_${task.id}_duration`] !== undefined
          ? Number(formValues[`task_${task.id}_duration`]) || 0
          : task.duration || 0;

      // Get downtime from form first, then fallback to state
      const downtime =
        formValues[`task_${task.id}_downtime`] !== undefined
          ? Number(formValues[`task_${task.id}_downtime`]) || 0
          : task.downtime || 0;

      totalTAT += duration;
      totalDowntime += downtime;
    });

    return { totalTAT, totalDowntime };
  }, [state.tasks, tasksForm.watch()]); // ✅ Watch form changes

  // ✅ Enhanced Transform tasks data to API format with totals
  const transformTasksToApiFormat = useCallback(
    (tasks: any[]): UpdateSopTasksRequest => {
      // Filter out empty tasks and transform to API format
      const transformedTasks = tasks
        .filter((task) => {
          // Only include tasks that have at least a title
          return task.title && task.title.trim() !== "";
        })
        .map((task) => {
          const skillIds: number[] = task.skills || [];

          const transformedTask = {
            name: task.title || "",
            time: task.duration?.toString() || "0",
            downtime: task.downtime?.toString() || "0",
            skillId: skillIds, // ✅ Use camelCase to match TypeScript type
            isPhotoReq: task.photoRequired ? "Yes" : "No", // ✅ Use camelCase to match TypeScript type
          };

          return transformedTask;
        });

      // ✅ Calculate totals for API using current totals
      const apiData = {
        task: transformedTasks,
        remoteUtilityId: parseInt(utilityId || "0"), // ✅ Use camelCase to match TypeScript type
        task_time_count: totalTAT, // ✅ Use calculated total TAT
        total_downtime: totalDowntime, // ✅ Use calculated total downtime
        taskDefinitionUpdated: true,
      };

      return apiData;
    },
    [utilityId, totalTAT, totalDowntime]
  );

  // Track if API tasks have been loaded for this operation type
  const [lastLoadedOperationType, setLastLoadedOperationType] = useState("");
  const [apiTasksLoaded, setApiTasksLoaded] = useState(false);

  // Effect to load API tasks when operation type changes
  useEffect(() => {
    if (
      operationType &&
      operationType !== lastLoadedOperationType &&
      apiTasks &&
      apiTasks.length > 0 &&
      !loadingApiTasks &&
      skillsOptions &&
      skillsOptions.length > 0
    ) {
      setLastLoadedOperationType(operationType);
      setApiTasksLoaded(true);
    }
  }, [
    operationType,
    apiTasks,
    loadingApiTasks,
    lastLoadedOperationType,
    skillsOptions,
  ]);

  // ✅ FIXED: Improved form initialization and population
  useEffect(() => {
    if (state.tasks && skillsOptions && skillsOptions.length > 0) {
      if (isManualAdd || isManualDelete) {
        setIsManualAdd(false); // Reset the flags
        setIsManualDelete(false);
        return; // Don't reset the form
      }
      const defaultValues = createDefaultValues();

      // Always reset form with current state values
      tasksForm.reset(defaultValues);
      setIsFormInitialized(true);

      console.log("🔄 Form reset with values:", defaultValues);
    }
  }, [state.tasks, skillsOptions, createDefaultValues, tasksForm]);

  // ✅ FIXED: Function to handle card selection with proper form sync
  const handleCardSelect = useCallback(
    (taskType: any) => {
      console.log("🔍 Card selected:", taskType);
      const skillIds = taskType.skillIds || [];

      console.log("🔍 Mapped skills:", {
        skillIds: skillIds,
        skillsOptions: skillsOptions?.slice(0, 3),
      });

      // Create a new task based on the selected card
      const newTask = {
        id: Date.now(),
        title: taskType.label || "",
        description: taskType.description || "",
        duration: parseInt(taskType.time) || 0,
        downtime: parseInt(taskType.downtime) || 0,
        skills: skillIds, // ✅ Already an array of skill names
        skillIds: taskType.skillIds || [],
        mandatory: true,
        photoRequired: taskType.photoRequired || false,
        isFromApi: true,
        apiCode: taskType.value,
      };

      // Add the task to state
      const currentTasks = state.tasks || [];
      const updatedTasks = [...currentTasks, newTask];

      // Update state first
      onUpdateState({
        tasks: updatedTasks,
      });

      // ✅ Set form values immediately
      const taskFieldValues = {
        [`task_${newTask.id}_title`]: newTask.title,
        [`task_${newTask.id}_duration`]: newTask.duration,
        [`task_${newTask.id}_downtime`]: newTask.downtime,
        [`task_${newTask.id}_skills`]: skillIds, // ✅ Array of skill names
        [`task_${newTask.id}_mandatory`]: newTask.mandatory,
        [`task_${newTask.id}_photoRequired`]: newTask.photoRequired,
      };

      // Set all form values at once
      Object.entries(taskFieldValues).forEach(([key, value]) => {
        tasksForm.setValue(key, value, {
          shouldDirty: true,
          shouldValidate: true,
        });
      });

      console.log("🔍 Form values set:", taskFieldValues);
    },
    [state.tasks, onUpdateState, tasksForm, getSkillNameById, skillsOptions]
  );

  // Get skill level color based on duration or skill complexity
  const getSkillLevelColor = (taskType: any) => {
    const duration = parseInt(taskType.time) || 0;
    const skillCount = taskType.skillIds?.length || 0;

    // Determine complexity based on duration and skill requirements
    if (duration <= 10 || skillCount <= 1) {
      return { level: "basic", color: "text-blue-600 bg-blue-100" };
    } else if (duration <= 25 || skillCount <= 2) {
      return { level: "standard", color: "text-orange-600 bg-orange-100" };
    } else {
      return { level: "complex", color: "text-purple-600 bg-purple-100" };
    }
  };

  // Render task type cards
  const renderTaskCards = () => {
    if (loadingApiTasks) {
      return (
        <div className="space-y-4">
          {/* Filter buttons skeleton */}
          <div className="flex gap-2 mb-6">
            {["All", "Basic", "Standard", "Complex"].map((filter) => (
              <div
                key={filter}
                className="h-8 w-16 bg-gray-300 rounded animate-pulse"
              ></div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="border border-gray-200 rounded-lg p-6 animate-pulse"
              >
                <div className="h-6 bg-gray-300 rounded mb-2"></div>
                <div className="h-4 bg-gray-300 rounded mb-4"></div>
                <div className="h-4 bg-gray-300 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (apiTasksError) {
      return (
        <div className="p-6 border border-red-200 rounded-lg bg-red-50">
          <p className="text-red-800">
            Error loading task types. Please try again.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.location.reload()}
            className="mt-2"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </Button>
        </div>
      );
    }

    if (!apiTasks || apiTasks.length === 0) {
      return (
        <div className="p-6 border border-gray-200 rounded-lg bg-gray-50">
          <p className="text-gray-600">
            No task types available for the selected operation type.
          </p>
        </div>
      );
    }

    // Filter tasks based on selected filter
    const filteredTasks =
      selectedFilter === "all"
        ? apiTasks
        : apiTasks.filter((task) => {
            const skillInfo = getSkillLevelColor(task);
            return skillInfo.level === selectedFilter.toLowerCase();
          });

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTasks.map((taskType) => {
            const skillInfo = getSkillLevelColor(taskType);
            return (
              <div
                key={taskType.value}
                className="border border-gray-200 rounded-lg p-6 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer bg-white"
                onClick={() => handleCardSelect(taskType)}
              >
                <div className="flex justify-between items-start mb-3">
                  <h3 className="font-semibold text-lg text-gray-900">
                    {taskType.label}
                  </h3>
                  <span className="text-sm text-gray-500">
                    {taskType.time ? `${taskType.time} min` : ""}
                  </span>
                </div>

                {taskType.description && (
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {taskType.description}
                  </p>
                )}

                <div className="flex items-center justify-between">
                  <span
                    className={`px-2 py-1 text-xs font-medium rounded-full ${skillInfo.color}`}
                  >
                    {skillInfo.level}
                  </span>
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                </div>
              </div>
            );
          })}
        </div>

        {filteredTasks.length === 0 && selectedFilter !== "all" && (
          <div className="p-6 border border-gray-200 rounded-lg bg-gray-50">
            <p className="text-gray-600">
              No {selectedFilter} tasks available.
            </p>
          </div>
        )}
      </div>
    );
  };

  // Define form fields dynamically
  const allFormFields: FormField[] = useMemo(() => {
    const fields: FormField[] = [];

    // Add task fields dynamically
    const tasks = state.tasks || [];
    if (tasks.length > 0) {
      // Add tasks section header
      fields.push({
        name: "tasksHeader",
        label: "Task Configuration",
        type: "label",
        disabled: true,
        value: "",
        className: "col-span-6",
        classes: {
          container: "w-full border-t pt-6",
          label: "block text-lg font-semibold text-gray-700",
        },
        fullWidth: true,
      });

      tasks.forEach((task: any, index: number) => {
        // Task header with remove button
        fields.push({
          name: `task_${task.id}_header`,
          label: ``,
          type: "text",
          disabled: true,
          value: "",
          customRender: () => (
            <div className="flex justify-between items-center mb-3">
              <div className="flex items-center gap-2">
                <h3 className="font-medium text-base">Task {index + 1}</h3>
                {task.isFromApi && (
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                    Template
                  </span>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeTask(task.id)}
                disabled={(state.tasks?.length || 0) <= 1 || readOnly}
                type="button"
              >
                <Trash2 size={16} />
              </Button>
            </div>
          ),
          className: "col-span-6",
          classes: {
            container: `w-full border rounded-lg p-4 mb-4 ${
              task.isFromApi ? "border-blue-200 bg-blue-50" : "border-gray-200"
            }`,
          },
          fullWidth: true,
        });

        // Task title and duration (2 columns)
        fields.push(
          {
            name: `task_${task.id}_title`,
            label: "Task Name",
            type: "text",
            required: true,
            placeholder: "Task name",
            value: task.title || "",
            disabled: readOnly || (task.isFromApi && !task.allowEdit),
            onChange: (value: string) => updateTask(task.id, "title", value),
            className: "col-span-3",
            classes: {
              container: "w-full mb-4",
              label: "block text-sm font-medium text-gray-700 mb-2",
              input: `w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors ${
                task.isFromApi ? "bg-blue-50" : "bg-white"
              }`,
              error: "text-red-600 text-sm mt-1",
            },
          },
          {
            name: `task_${task.id}_duration`,
            label: "Turnaround Time (minutes)",
            type: "number",
            required: true,
            placeholder: "Duration in minutes",
            value: task.duration || 0,
            min: 0, 
            disabled: readOnly,
            onChange: (value: number) =>
              updateTask(task.id, "duration", value || 0),
            className: "col-span-3",
            classes: {
              container: "w-full mb-4",
              label: "block text-sm font-medium text-gray-700 mb-2",
              input: `w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors ${
                task.isFromApi ? "bg-blue-50" : "bg-white"
              }`,
              error: "text-red-600 text-sm mt-1",
            },
          }
        );

        // Downtime and skills (2 columns)
        fields.push(
          {
            name: `task_${task.id}_downtime`,
            label: "Downtime (Mins)",
            type: "number",
            placeholder: "Downtime in minutes",
            value: task.downtime || 0,
            disabled: readOnly,
            required: true, // ✅ Make it required
            min: 0, // ✅ Minimum value
            onChange: (value: number) =>
              updateTask(task.id, "downtime", value || 0),
            className: "col-span-3",
            classes: {
              container: "w-full mb-4",
              label: "block text-sm font-medium text-gray-700 mb-2",
              input: `w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors ${
                task.isFromApi ? "bg-blue-50" : "bg-white"
              }`,
              error: "text-red-600 text-sm mt-1",
            },
          },
          {
            name: `task_${task.id}_skills`,
            label: "Skills",
            type: "multi-select",
            options: skillsOptions,
            placeholder: loadingSkills
              ? "Loading skills..."
              : "Select required skill",
            value: task.skills || [],
            disabled: readOnly || loadingSkills,
            onChange: (value: string[]) => {
              updateTask(task.id, "skills", value);
            },
            className: "col-span-3",
            classes: {
              container: "w-full mb-4",
              label: "block text-sm font-medium text-gray-700 mb-2",
              select: `w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors cursor-pointer ${
                readOnly || loadingSkills
                  ? "bg-gray-50 cursor-not-allowed"
                  : task.isFromApi
                  ? "bg-blue-50"
                  : "bg-white"
              }`,
              error: "text-red-600 text-sm mt-1",
            },
          }
        );

        // Checkboxes (2 columns)
        fields.push(
          {
            name: `task_${task.id}_mandatory`,
            label: "This task is mandatory",
            type: "checkbox",
            value: task.mandatory || false,
            disabled: readOnly || (task.isFromApi && !task.allowEdit),
            onChange: (value: boolean) =>
              updateTask(task.id, "mandatory", value),
            className: "col-span-3",
            classes: {
              container: "w-full mb-4",
              label: "text-sm font-medium",
            },
          },
          {
            name: `task_${task.id}_photoRequired`,
            label: "Photo Required",
            type: "checkbox",
            value: task.photoRequired || false,
            disabled: readOnly,
            icon: <Camera size={16} className="ml-1 text-gray-500" />,
            onChange: (value: boolean) =>
              updateTask(task.id, "photoRequired", value),
            className: "col-span-3",
            classes: {
              container: "w-full mb-4",
              label: "text-sm font-medium flex items-center",
            },
          }
        );
      });
    }

    return fields;
  }, [state.tasks, readOnly, skillsOptions, loadingSkills, getSkillNameById]);

  // ✅ FIXED: Add task with proper form handling
  const addTask = useCallback(() => {
    const currentTasks = state.tasks || [];
    const newTask = {
      id:
        currentTasks.length > 0
          ? Math.max(...currentTasks.map((t: any) => t.id)) + 1
          : 1,
      title: "",
      duration: 0,
      mandatory: false,
      downtime: 0,
      skills: [],
      photoRequired: false,
      isFromApi: false,
    };
    setIsManualAdd(true);

    const updatedTasks = [...currentTasks, newTask];
    onUpdateState({
      tasks: updatedTasks,
    });

    // ✅ Set form values immediately
    const taskFieldValues = {
      [`task_${newTask.id}_title`]: "",
      [`task_${newTask.id}_duration`]: 0,
      [`task_${newTask.id}_downtime`]: 0,
      [`task_${newTask.id}_skills`]: [],
      [`task_${newTask.id}_mandatory`]: false,
      [`task_${newTask.id}_photoRequired`]: false,
    };

    Object.entries(taskFieldValues).forEach(([key, value]) => {
      tasksForm.setValue(key, value, { shouldDirty: true });
    });
  }, [state.tasks, onUpdateState, tasksForm]);

  const removeTask = useCallback(
    (id: number) => {
      const currentTasks = state.tasks || [];
      const updatedTasks = currentTasks.filter((task: any) => task.id !== id);
      setIsManualDelete(true);
      onUpdateState({
        tasks: updatedTasks,
      });
    },
    [state.tasks, onUpdateState]
  );

  // ✅ Enhanced updateTask function with better form sync
  const updateTask = useCallback(
    (id: number, field: string, value: any) => {
      const currentTasks = state.tasks || [];
      const updatedTasks = currentTasks.map((task: any) =>
        task.id === id ? { ...task, [field]: value } : task
      );

      // Update state
      onUpdateState({
        tasks: updatedTasks,
      });

      // Also update the form value immediately to prevent clearing
      tasksForm.setValue(`task_${id}_${field}`, value, { shouldDirty: true });
    },
    [state.tasks, onUpdateState, tasksForm]
  );

  // ✅ Enhanced Form submit with proper form data capture
  const handleFormSubmit = useCallback(
    async (data: any) => {
      if (!sopId) {
        toast({
          title: "Error",
          description:
            "SOP ID is missing. Please complete the Basic Information step first.",
          variant: "destructive",
          duration: 5000,
        });
        return;
      }

      if (!utilityId) {
        toast({
          title: "Error",
          description: "Utility ID is missing.",
          variant: "destructive",
          duration: 5000,
        });
        return;
      }

      try {
        // ✅ Get form values and merge with state tasks
        const formValues = tasksForm.getValues();
        const currentTasks = state.tasks || [];

        if (currentTasks.length === 0) {
          toast({
            title: "No Tasks",
            description: "Please add at least one task before proceeding.",
            variant: "destructive",
            duration: 5000,
          });
          return;
        }

        // ✅ Update tasks with current form values
        const updatedTasks = currentTasks.map((task: any) => {
          const taskTitle =
            formValues[`task_${task.id}_title`] !== undefined
              ? formValues[`task_${task.id}_title`]
              : task.title || "";
          const formSkills = formValues[`task_${task.id}_skills`];

          return {
            ...task,
            title: taskTitle,
            duration:
              formValues[`task_${task.id}_duration`] !== undefined
                ? formValues[`task_${task.id}_duration`]
                : task.duration || 0,
            downtime:
              formValues[`task_${task.id}_downtime`] !== undefined
                ? formValues[`task_${task.id}_downtime`]
                : task.downtime || 0,
            skills: formSkills !== undefined ? formSkills : task.skills || [],
            mandatory:
              formValues[`task_${task.id}_mandatory`] !== undefined
                ? formValues[`task_${task.id}_mandatory`]
                : task.mandatory || false,
            photoRequired:
              formValues[`task_${task.id}_photoRequired`] !== undefined
                ? formValues[`task_${task.id}_photoRequired`]
                : task.photoRequired || false,
          };
        });

        // ✅ FIXED: Better validation - check both form values and state
        const validTasks = updatedTasks.filter((task: any) => {
          const hasTitle = task.title && task.title.trim() !== "";
          console.log(
            `Task ${task.id} - title: "${task.title}" - valid: ${hasTitle}`
          );
          return hasTitle;
        });

        if (validTasks.length === 0) {
          toast({
            title: "Invalid Tasks",
            description: "Please ensure at least one task has a title.",
            variant: "destructive",
            duration: 5000,
          });
          return;
        }

        const apiData = transformTasksToApiFormat(updatedTasks);

        const response = await updateSopTasksMutation.mutateAsync({
          sopId: sopId,
          data: apiData,
        });
        if (onStepSave) {
          await onStepSave(
            2,
            {
              // step index 2 for tasks
              tasks: updatedTasks,
            },
            true
          );
        }
        toast({
          title: "Tasks Updated Successfully",
          description: `${validTasks.length} tasks have been added to the SOP.`,
          duration: 3000,
        });

        // Update local state with the updated tasks
        onUpdateState({
          tasks: updatedTasks,
        });

        if (onNext) {
          onNext();
        }
      } catch (error: any) {
        toast({
          title: "Failed to Update Tasks",
          description:
            error?.message ||
            "An error occurred while updating tasks. Please try again.",
          variant: "destructive",
          duration: 5000,
        });
      }
    },
    [
      sopId,
      utilityId,
      state.tasks,
      tasksForm,
      transformTasksToApiFormat,
      updateSopTasksMutation,
      onUpdateState,
      onNext,
      toast,
      onStepSave,
    ]
  );

  const handleFieldChange = useCallback((fieldName: string, value: any) => {
    // Handle any field changes if needed
  }, []);

  const renderCustomFooter = useCallback(() => {
    if (readOnly) return null;

    const isSubmitting = updateSopTasksMutation.isPending;

    return (
      <div className="space-y-4">
        {/* Show warning if no SOP ID */}
        {!sopId && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-800">
              <strong>Warning:</strong> No SOP ID found. Please complete the
              Basic Information step first.
            </p>
          </div>
        )}

        {/* Add Task Button */}
        <Button
          variant="outline"
          className="w-full"
          onClick={addTask}
          type="button"
          disabled={isSubmitting}
        >
          <Plus size={16} className="mr-1" /> Add Manual Task
        </Button>

        {/* Navigation */}
        <div className="pt-6 flex justify-between border-t border-gray-200">
          <Button
            variant="outline"
            onClick={onPrevious}
            type="button"
            disabled={isSubmitting}
            className="flex items-center gap-2 px-6 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            Back
          </Button>

          <Button
            type="submit"
            disabled={isSubmitting || !sopId}
            className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Updating Tasks...
              </>
            ) : (
              <>
                Next <ChevronRight className="h-4 w-4" />
              </>
            )}
          </Button>
        </div>
      </div>
    );
  }, [readOnly, onPrevious, addTask, updateSopTasksMutation.isPending, sopId]);

  // ✅ Debug logging for troubleshooting
  useEffect(() => {
    console.log("🔍 Debug Info:", {
      tasksCount: state.tasks?.length || 0,
      isFormInitialized,
      skillsOptionsCount: skillsOptions?.length || 0,
      formValues: tasksForm.getValues(),
    });
  }, [state.tasks, isFormInitialized, skillsOptions, tasksForm]);

  return (
    <div className="space-y-6">
      {/* Show message if no operation type selected */}
      {!operationType && (
        <div className="p-4 border border-yellow-200 rounded-lg bg-yellow-50">
          <p className="text-yellow-800">
            Please select an Operation Type in the Basic Information step to
            load available tasks.
          </p>
        </div>
      )}

      {/* Show message if skills are loading */}
      {loadingSkills && (
        <div className="p-4 border border-blue-200 rounded-lg bg-blue-50">
          <p className="text-blue-800">Loading skills from API...</p>
        </div>
      )}

      {/* Main Content */}
      {/* Always show task type cards at the top */}
      <div>
        <div className="mb-6">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Task Templates
              </h2>
              <p className="text-gray-600">
                Select from pre-defined task templates or create your own
              </p>
            </div>

            {/* ✅ Total TAT and Downtime Display */}
            {state.tasks && state.tasks.length > 0 && (
              <div className="flex gap-4">
                <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                  <Clock className="h-4 w-4 text-blue-600" />
                  <div className="text-sm">
                    <span className="font-medium text-blue-900">
                      Total TAT:
                    </span>
                    <span className="ml-1 text-blue-700">{totalTAT} mins</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 px-3 py-2 bg-orange-50 border border-orange-200 rounded-lg">
                  <Pause className="h-4 w-4 text-orange-600" />
                  <div className="text-sm">
                    <span className="font-medium text-orange-900">
                      Total Downtime:
                    </span>
                    <span className="ml-1 text-orange-700">
                      {totalDowntime} mins
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        {renderTaskCards()}
      </div>

      {/* Always show the DynamicForm and footer, even if there are no tasks */}
      <div>
        {state.tasks && state.tasks.length > 0 && (
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Tasks</h2>
        )}
        {isFormInitialized && (
          <DynamicForm
            fields={allFormFields}
            form={tasksForm}
            onSubmit={handleFormSubmit}
            onFieldChange={handleFieldChange}
            config={{
              showSubmitButton: false,
              showResetButton: false,
              classes: {
                form: "space-y-6",
                button: "px-4 py-2 rounded-md font-medium transition-colors",
                buttonContainer:
                  "flex justify-end gap-3 pt-6 border-gray-200 mt-8",
                input:
                  "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
                textarea:
                  "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical",
                select:
                  "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white",
                label: "block text-sm font-medium text-gray-700 mb-1",
                error: "text-sm text-red-600 mt-1",
                container: "space-y-1",
              },
            }}
            gridColumns={6}
            responsiveColumns={{ sm: 1, md: 1 }}
            renderCustomFooter={renderCustomFooter}
            className="space-y-6"
          />
        )}
      </div>
    </div>
  );
};

export default SopTasksStep;
